import { ArtistTaxInfoAttachmentInit, ArtistTaxInfoFileType, ArtistTaxInfoProperty, NationalityType } from 'src/constant/artistTaxInfoConstants';
import { ArtistTaxInfoAttachmentModel } from 'src/models/TaxCenterModel';
import { Button, Col, DatePicker, Form, Input, Radio, Row, Upload, UploadProps, message } from 'antd';
import Title from 'antd/es/typography/Title';
import { useEffect, useState } from 'react';
import { UploadFileConstants, UploadListType, UploadValidationMessages } from 'src/constant/uploadFileConstants';
import { StringConstants } from 'src/constant/stringConstants';
import { UploadOutlined } from '@ant-design/icons';
import TranslationKey from 'src/i18n/translation';
import { addFileToS3 } from 'src/helper/s3UploadHelper';
import { RegexPattern } from 'src/constant/constants';

interface IndividualTaxCenterProps {
  nationalityType: string,
  idCardAttachment: ArtistTaxInfoAttachmentModel,
  certificationAttachment: ArtistTaxInfoAttachmentModel,
  onUploadFileChanged: (
    fileType: ArtistTaxInfoFileType,
    file: ArtistTaxInfoAttachmentModel) => void;
}

export default function IndividualTaxCenter(props: IndividualTaxCenterProps) {
  const [isThai, setIsThai] = useState(false);
  const [idCardFile, setIdCardFile] = useState<ArtistTaxInfoAttachmentModel>(ArtistTaxInfoAttachmentInit.defaultFileValue);
  const [certificationFile, setCertificationFile] = useState<ArtistTaxInfoAttachmentModel>(ArtistTaxInfoAttachmentInit.defaultFileValue);
  const foreignerFormUrl = 'https://www.lanl.gov/business/vendors/_assets/docs/fw8ben-form.pdf';

  useEffect(() => {
    if (props.nationalityType) {
      setIsThai(props.nationalityType === NationalityType.Thai);
    }
  }, [props.nationalityType]);

  useEffect(() => {
    if (props.idCardAttachment) {
      setIdCardFile(props.idCardAttachment);
    }
  }, [props.idCardAttachment]);

  useEffect(() => {
    if (props.certificationAttachment) {
      setCertificationFile(props.certificationAttachment);
    }
  }, [props.certificationAttachment]);

  const deleteFileFromS3 = async (fileName: string) => {
    // const params = {
    //   Bucket: S3Constants.BUCKET,
    //   Key: fileName,
    // };
    // 
    // TODO: call backend to delete file from S3
    // await s3Bucket.deleteObject(params, (err) => {
    //   if (err) {
    //     console.log(err, err.stack);
    //     message.error(`${fileName} file remove failed.`);
    //   }
    // });
  };

  const validateUploadFile = async (file: File, onError: any) => {
    const unknowFileType = !file.type || file.type === StringConstants.EMPTY;
    const fileTypeNotSupport = !UploadFileConstants.MIME_RELEASES_ACCEPT.includes(file.type);

    if (unknowFileType || fileTypeNotSupport) {
      const errorMessage = file.name + StringConstants.SPACE + UploadValidationMessages.INVALID_FILE_TYPE;
      onError(errorMessage);

      return errorMessage;
    }

    return StringConstants.EMPTY.toString();
  };

  const uploadPropsIdCard: UploadProps = {
    multiple: false,
    listType: UploadListType.TEXT,
    accept: UploadFileConstants.MIME_RELEASES_ACCEPT.toString(),
    maxCount: 1,
    showUploadList: true,
    async onRemove() {
      deleteFileFromS3(idCardFile.fileName);
    },
    async customRequest({ file, onError, onSuccess, onProgress }) {
      const originalFile = file as File;
      let errorMessage = await validateUploadFile(originalFile, onError);

      if (errorMessage !== StringConstants.EMPTY) {
        message.error(errorMessage, 10);
        return;
      }

      try {
        const fileKey = await addFileToS3(
          ArtistTaxInfoFileType.idCardAttachment,
          originalFile,
          onProgress);

        onSuccess?.(fileKey);

        var fileUploaded = {
          url: `${process.env.REACT_APP_S3_BUCKET}${fileKey}`,
          fileNameOriginal: originalFile.name,
          fileName: fileKey
        };

        setIdCardFile(fileUploaded);

        props.onUploadFileChanged(
          ArtistTaxInfoFileType.idCardAttachment,
          fileUploaded);

      } catch (err: any) {
        onError?.(err);
      }
    },
  };

  const uploadPropsCertification: UploadProps = {
    multiple: false,
    listType: UploadListType.TEXT,
    accept: UploadFileConstants.MIME_RELEASES_ACCEPT.toString(),
    maxCount: 1,
    showUploadList: true,
    async onRemove() {
      deleteFileFromS3(certificationFile.fileName);
    },
    async customRequest({
      file,
      onError,
      onSuccess,
      onProgress,
    }) {
      const originalFile = file as File;

      const errorMessage =
        await validateUploadFile(
          originalFile,
          onError);

      if (errorMessage !== StringConstants.EMPTY) {
        message.error(errorMessage, 10);
        return;
      }

      try {
        const fileKey = await addFileToS3(
          ArtistTaxInfoFileType.certification,
          originalFile,
          onProgress);

        onSuccess?.(fileKey);

        var fileUploaded = {
          url: `${process.env.REACT_APP_S3_BUCKET}${fileKey}`,
          fileNameOriginal: originalFile.name,
          fileName: fileKey
        };

        setCertificationFile(fileUploaded);

        props.onUploadFileChanged(
          ArtistTaxInfoFileType.certification,
          fileUploaded);

      } catch (err: any) {
        onError?.(err);
      }
    },
  };

  return (
    <>
      <Row>
        <Col span={24} className='mb-4'>
          <Title level={2}>{TranslationKey('taxCenter.pleaseComplete')}</Title>
          <p>{TranslationKey('taxCenter.basedOnTheInformation')}</p>
          <p>{TranslationKey('taxCenter.youMayWantToReview')}</p>
          <p><span className='text-danger'>*</span> {TranslationKey('taxCenter.markedFields')}</p>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={24}>
          <Title level={3}>
            {TranslationKey('taxCenter.part1')}: {TranslationKey('taxCenter.identificationOfBeneficial')}
          </Title>
          <p>{TranslationKey('taxCenter.nameAs')}<span className='text-danger'>*</span></p>
        </Col>
        <Col md={12} sm={24}>
          <Form.Item
            label={TranslationKey('taxCenter.yourFullName')}
            name="fullName"
            rules={[{
              required: true,
              message: TranslationKey('taxCenter.requireYourFullName')
            }]}>
            <Input
              size='large'
              className='w-100'
            />
          </Form.Item>
        </Col>
        <Col md={12} sm={24}>
          {
            isThai
              ? (
                <Form.Item
                  label={TranslationKey('taxCenter.idCardNumber')}
                  name="idCardNumber"
                  rules={[
                    {
                      required: true,
                      message: TranslationKey('taxCenter.requireIdCardNumber')
                    },
                    {
                      pattern: RegexPattern.number,
                      message: TranslationKey('taxCenter.requireNumberOnly')
                    },
                  ]}>
                  <Input
                    size='large'
                    className='w-100'
                    maxLength={13}
                    onKeyDown={e => {
                      if (
                        !RegexPattern.number.test(e.key) &&
                        !['Backspace'].includes(e.key)
                      ) {
                        e.preventDefault();
                      }
                    }}
                    onPaste={e => {
                      const paste = e.clipboardData.getData('text');

                      if (!RegexPattern.number.test(paste)) {
                        e.preventDefault();
                      }
                    }}
                  />
                </Form.Item>
              )
              : (
                <Form.Item
                  label={TranslationKey('taxCenter.countryOfCitizenship')}
                  name="countryOfCitizenship">
                  <Input
                    size='large'
                    className='w-100'
                  />
                </Form.Item>
              )
          }
        </Col>
        <Col span={24}>
          <Title level={3}>
            {
              isThai
                ? TranslationKey('taxCenter.address')
                : TranslationKey('taxCenter.permanentResidenceAddress')
            }
          </Title>
        </Col>
        <Col md={12} sm={24}>
          <Form.Item
            label={TranslationKey('taxCenter.addressLine1')}
            name="address1"
            rules={[
              {
                required: isThai,
                message: TranslationKey('taxCenter.requireAddressLine1')
              }
            ]}>
            <Input
              size='large'
              className='w-100'
            />
          </Form.Item>
        </Col>
        <Col md={12} sm={24}>
          <Form.Item
            label={TranslationKey('taxCenter.addressLine2')}
            name="address2"
            rules={[
              {
                required: isThai,
                message: TranslationKey('taxCenter.requireAddressLine2')
              }]}>
            <Input
              size='large'
              className='w-100'
            />
          </Form.Item>
        </Col>
        <Col md={12} sm={24}>
          <Form.Item
            label={TranslationKey('taxCenter.city')}
            name="city"
            rules={[{
              required: isThai,
              message: TranslationKey('taxCenter.requireCity')
            }]}>
            <Input
              size='large'
              className='w-100'
            />
          </Form.Item>
        </Col>
        <Col md={12} sm={24}>
          <Form.Item
            label={TranslationKey('taxCenter.postal')}
            name="postcode"
            rules={[
              {
                required: isThai,
                message: TranslationKey('taxCenter.requirePostal')
              },
              {
                pattern: RegexPattern.number,
                message: TranslationKey('taxCenter.requireNumberOnly')
              },
            ]}>
            <Input
              size='large'
              className='w-100'
              maxLength={10}
              onKeyDown={e => {
                if (
                  !RegexPattern.number.test(e.key) &&
                  !['Backspace'].includes(e.key)
                ) {
                  e.preventDefault();
                }
              }}
              onPaste={e => {
                const paste = e.clipboardData.getData('text');

                if (!RegexPattern.number.test(paste)) {
                  e.preventDefault();
                }
              }}
            />
          </Form.Item>
        </Col>
        {
          isThai
            ? (
              <>
                <Col md={12} sm={24}>
                  <Form.Item
                    label={TranslationKey('email')}
                    name="email"
                    rules={[
                      {
                        required: true,
                        message: TranslationKey('requiredEmail')
                      }]}>
                    <Input
                      size='large'
                      className='w-100'
                    />
                  </Form.Item>
                </Col>
                <Col md={12} sm={24}>
                  <Form.Item
                    label={TranslationKey('taxCenter.phoneNumber')}
                    name="phone"
                    rules={[
                      {
                        required: true,
                        message: TranslationKey('taxCenter.requirePhoneNumber')
                      },
                      {
                        pattern: RegexPattern.number,
                        message: TranslationKey('taxCenter.requireNumberOnly')
                      },
                    ]}>
                    <Input
                      size='large'
                      className='w-100'
                      maxLength={10}
                      onKeyDown={e => {
                        if (
                          !RegexPattern.number.test(e.key) &&
                          !['Backspace'].includes(e.key)
                        ) {
                          e.preventDefault();
                        }
                      }}
                      onPaste={e => {
                        const paste = e.clipboardData.getData('text');

                        if (!RegexPattern.number.test(paste)) {
                          e.preventDefault();
                        }
                      }}
                    />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Title level={3}>
                    {TranslationKey('taxCenter.uploadIDCard')}
                  </Title>
                </Col>
                <Col span={24}>
                  <Form.Item
                    name={ArtistTaxInfoProperty.idCardAttachmentUrl}
                    className='mt-4 text-light '
                    label={TranslationKey('taxCenter.idCard')}>
                    <div className='upload-form-box'>
                      {
                        idCardFile && idCardFile.url !== StringConstants.EMPTY
                          ?
                          <div className='d-flex gap-2'>
                            <Button type='default' size='small'
                              onClick={() => {
                                setIdCardFile(ArtistTaxInfoAttachmentInit.defaultFileValue);
                                props.onUploadFileChanged(
                                  ArtistTaxInfoFileType.idCardAttachment,
                                  ArtistTaxInfoAttachmentInit.defaultFileValue);
                              }}
                              danger>
                              {TranslationKey('button.newUpload')}
                            </Button>
                            <a href={idCardFile.url} target='_blank' rel="noreferrer">
                              <span style={{ color: 'black' }}>{idCardFile.fileNameOriginal}</span>
                            </a>
                          </div>
                          :
                          <Upload {...uploadPropsIdCard}>
                            <Button icon={<UploadOutlined />}>{TranslationKey('button.clickToUpload')}</Button>
                          </Upload>
                      }
                    </div>
                  </Form.Item>
                  <Form.Item name={ArtistTaxInfoProperty.idCardAttachmentFileName} hidden></Form.Item>
                  <Form.Item name={ArtistTaxInfoProperty.idCardAttachmentFileNameOriginal} hidden></Form.Item>
                </Col>
              </>
            )
            : (
              <>
                <Col md={12} sm={24}>
                  <Form.Item
                    label={TranslationKey('taxCenter.isPoBox')}
                    name="poBoxType"
                    rules={[{ required: true }]}>
                    <Radio.Group defaultValue={true}>
                      <Radio value={true}>
                        {TranslationKey('button.yes')}
                      </Radio>
                      <Radio value={false}>
                        {TranslationKey('button.no')}
                      </Radio>
                    </Radio.Group>
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Title level={3}>
                    {TranslationKey('taxCenter.mailingAddress')}
                  </Title>
                </Col>
                <Col md={12} sm={24}>
                  <Form.Item
                    label={TranslationKey('taxCenter.address1')}
                    name="mailingAddress1">
                    <Input
                      size='large'
                      className='w-100'
                    />
                  </Form.Item>
                </Col>
                <Col md={12} sm={24}>
                  <Form.Item
                    label={TranslationKey('taxCenter.address2')}
                    name="mailingAddress2">
                    <Input
                      size='large'
                      className='w-100'
                    />
                  </Form.Item>
                </Col>
                <Col md={12} sm={24}>
                  <Form.Item
                    label={TranslationKey('taxCenter.city')}
                    name="mailingCity">
                    <Input
                      size='large'
                      className='w-100'
                    />
                  </Form.Item>
                </Col>
                <Col md={12} sm={24}>
                  <Form.Item
                    label={TranslationKey('taxCenter.postal')}
                    name="mailingPostcode"
                    rules={[
                      {
                        pattern: RegexPattern.number,
                        message: TranslationKey('taxCenter.requireNumberOnly')
                      },
                    ]}>
                    <Input
                      size='large'
                      className='w-100'
                      maxLength={10}
                      onKeyDown={e => {
                        if (
                          !RegexPattern.number.test(e.key) &&
                          !['Backspace'].includes(e.key)
                        ) {
                          e.preventDefault();
                        }
                      }}
                      onPaste={e => {
                        const paste = e.clipboardData.getData('text');

                        if (!RegexPattern.number.test(paste)) {
                          e.preventDefault();
                        }
                      }}
                    />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item
                    label={TranslationKey('taxCenter.usTaxPayerIdentification')}
                    name="usTaxPayerType"
                    rules={[{ required: true }]}>
                    <Radio.Group defaultValue={'SSN'}>
                      <Radio value={'SSN'}>SSN</Radio>
                      <Radio value={'ITIN'}>ITIN</Radio>
                    </Radio.Group>
                  </Form.Item>
                </Col>
                <Col md={12} sm={24}>
                  <Form.Item
                    label={TranslationKey('taxCenter.ssnOrItinNumber')}
                    name="usTaxPayerNumber">
                    <Input
                      size='large'
                      className='w-100'
                    />
                  </Form.Item>
                </Col>
                <Col md={12} sm={24}>
                  <Form.Item
                    label={TranslationKey('taxCenter.foreignTaxIdentificationnumber')}
                    name="taxIdNumber">
                    <Input
                      size='large'
                      className='w-100'
                    />
                  </Form.Item>
                </Col>
                <Col md={12} sm={24}>
                  <Form.Item
                    label={TranslationKey('taxCenter.referenceNumberIfApplicable')}
                    name="referenceNumber">
                    <Input
                      size='large'
                      className='w-100'
                    />
                  </Form.Item>
                </Col>
                <Col md={12} sm={24}>
                  <Form.Item
                    label={TranslationKey('taxCenter.dateOfBirth')}
                    name="dateOfBirth">
                    <DatePicker
                      size='large'
                      className='w-100'
                    />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Title level={3}>
                    {TranslationKey('taxCenter.part2')}: {TranslationKey('taxCenter.claimOfTaxTreatyBenefits')}
                  </Title>
                </Col>
                <Col span={24}>
                  <Form.Item
                    label={TranslationKey('taxCenter.iCertifyThatTheBeneficialOwnerIsAResidentOf')}
                    name="claimOfTax">
                    <Input
                      size='large'
                      className='w-100'
                    />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Title level={3}>
                    {TranslationKey('taxCenter.part3')}: {TranslationKey('taxCenter.certification')}
                  </Title>
                  <p>
                    {TranslationKey('taxCenter.pleaseDownloadThisFile')}
                    {StringConstants.SPACE}
                    <a
                      target="_blank"
                      href={foreignerFormUrl}>
                      {foreignerFormUrl}
                    </a>
                    {StringConstants.SPACE}
                    {TranslationKey('taxCenter.andFillFormAndUpload')}
                  </p>
                </Col>
                <Col md={12} sm={24}>
                  <Form.Item
                    name={ArtistTaxInfoProperty.certificationAttachmentUrl}
                    className='mt-4 text-light '
                    label={TranslationKey('taxCenter.certification')}>
                    <div className='upload-form-box'>
                      {
                        certificationFile && certificationFile.url !== StringConstants.EMPTY
                          ?
                          <div className='d-flex gap-2'>
                            <Button type='default' size='small'
                              onClick={() => {
                                setCertificationFile(ArtistTaxInfoAttachmentInit.defaultFileValue);
                                props.onUploadFileChanged(
                                  ArtistTaxInfoFileType.certification,
                                  ArtistTaxInfoAttachmentInit.defaultFileValue);
                              }}
                              danger>
                              {TranslationKey('button.upload')}
                            </Button>
                            <a href={certificationFile.url} target='_blank' rel="noreferrer">
                              <span style={{ color: 'black' }}>{certificationFile.fileNameOriginal}</span>
                            </a>
                          </div>
                          :
                          <Upload {...uploadPropsCertification}>
                            <Button icon={<UploadOutlined />}>
                              {TranslationKey('button.clickToUpload')}
                            </Button>
                          </Upload>
                      }
                    </div>
                  </Form.Item>
                  <Form.Item name={ArtistTaxInfoProperty.certificationAttachmentFileName} hidden></Form.Item>
                  <Form.Item name={ArtistTaxInfoProperty.certificationAttachmentFileNameOriginal} hidden></Form.Item>
                </Col>
              </>
            )
        }
      </Row>
    </>
  );
}
